
import React from 'react';
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Tables } from "@/integrations/supabase/types";
import NewQuoteForm from "./NewQuoteForm";
import QuoteFormModal from "./QuoteFormModal";
import QuoteTabsList from "./QuoteTabsList";
import QuoteTabContent from "./QuoteTabContent";
import { useQuoteFormModal } from "./useQuoteFormModal";

type Quote = Tables<'quotes'>;

interface QuoteTabsContainerProps {
  activeQuotes: Quote[];
  declinedQuotes: Quote[];
  isAdmin: boolean;
  canInteractWithQuotes: boolean;
  isAdminViewingOthersBooking: boolean;
  loadingQuoteId: string | null;
  generatingPayment: string | null;
  bookingId: string;
  onQuoteResponse: (quoteId: string, action: 'accepted' | 'rejected', response?: string) => void;
  onDeleteQuote: (quoteId: string) => void;
  onQuoteUpdate?: () => void;
}

const QuoteTabsContainer = ({
  activeQuotes,
  declinedQuotes,
  isAdmin,
  canInteractWithQuotes,
  isAdminViewingOthersBooking,
  loadingQuoteId,
  generatingPayment,
  bookingId,
  onQuoteResponse,
  onDeleteQuote,
  onQuoteUpdate
}: QuoteTabsContainerProps) => {
  const {
    quoteFormModal,
    handleReviseQuote,
    handleAddVariation,
    handleCloseModal
  } = useQuoteFormModal();

  const allQuotes = [...activeQuotes, ...declinedQuotes];

  return (
    <>
      <Tabs defaultValue="current" className="w-full">
        <QuoteTabsList
          currentQuotesCount={activeQuotes.length}
          declinedQuotesCount={declinedQuotes.length}
          allQuotesCount={allQuotes.length}
          isAdmin={isAdmin}
        />

        <QuoteTabContent
          value="current"
          quotes={activeQuotes}
          isAdmin={isAdmin}
          canInteractWithQuotes={canInteractWithQuotes}
          isAdminViewingOthersBooking={isAdminViewingOthersBooking}
          loadingQuoteId={loadingQuoteId}
          generatingPayment={generatingPayment}
          onQuoteResponse={onQuoteResponse}
          onDeleteQuote={onDeleteQuote}
          onReviseQuote={handleReviseQuote}
          onAddVariation={handleAddVariation}
          onPaymentUpdate={onQuoteUpdate}
          emptyMessage="No current quotes"
          bookingId={bookingId}
          showEmptyState={true}
        />

        <QuoteTabContent
          value="declined"
          quotes={declinedQuotes}
          isAdmin={isAdmin}
          canInteractWithQuotes={canInteractWithQuotes}
          isAdminViewingOthersBooking={isAdminViewingOthersBooking}
          loadingQuoteId={loadingQuoteId}
          generatingPayment={generatingPayment}
          onQuoteResponse={onQuoteResponse}
          onDeleteQuote={onDeleteQuote}
          onReviseQuote={handleReviseQuote}
          onAddVariation={handleAddVariation}
          onPaymentUpdate={onQuoteUpdate}
          emptyMessage="No declined quotes"
        />

        <QuoteTabContent
          value="history"
          quotes={allQuotes}
          isAdmin={isAdmin}
          canInteractWithQuotes={canInteractWithQuotes}
          isAdminViewingOthersBooking={isAdminViewingOthersBooking}
          loadingQuoteId={loadingQuoteId}
          generatingPayment={generatingPayment}
          onQuoteResponse={onQuoteResponse}
          onDeleteQuote={onDeleteQuote}
          onReviseQuote={handleReviseQuote}
          onAddVariation={handleAddVariation}
          onPaymentUpdate={onQuoteUpdate}
          emptyMessage="No quote history"
          showDescription={true}
        />

        {isAdmin && (
          <TabsContent value="new-quote" className="mt-4">
            <NewQuoteForm bookingId={bookingId} onQuoteCreated={() => onQuoteUpdate?.()} />
          </TabsContent>
        )}
      </Tabs>

      <QuoteFormModal
        isOpen={quoteFormModal.isOpen}
        onClose={handleCloseModal}
        bookingId={bookingId}
        mode={quoteFormModal.mode}
        parentQuote={quoteFormModal.parentQuote}
        onQuoteAdded={() => onQuoteUpdate?.()}
      />
    </>
  );
};

export default QuoteTabsContainer;
